if (!customElements.get('brp-cart-shipping-guard')) {
  const milliPounds = (value) => {
    const raw = String(value ?? '').trim();
    if (!/^\d+(?:\.\d{1,3})?$/.test(raw)) return null;
    const [whole, fraction = ''] = raw.split('.');
    const units = Number(whole) * 1000 + Number(fraction.padEnd(3, '0'));
    return Number.isSafeInteger(units) && units > 0 ? units : null;
  };
  const syncCountry = (country) => {
    document.querySelectorAll('[data-brp-shipping-country]').forEach((select) => {
      if (select.tagName === 'SELECT' && ![...select.options].some((option) => option.value === country)) select.add(new Option(country, country));
      select.value = country;
    });
  };
  const onCountrySelection = (event) => {
    if (!event.target.matches('[data-brp-shipping-country]')) return;
    document.documentElement.dataset.brpSelectedShippingCountry = event.target.value;
    syncCountry(event.target.value);
    document.querySelectorAll('brp-cart-shipping-guard').forEach((guard) => guard.update());
  };
  document.addEventListener('input', onCountrySelection, true);
  document.addEventListener('change', onCountrySelection, true);
  if (!customElements.get('brp-shipping-destination')) {
    customElements.define('brp-shipping-destination', class extends HTMLElement {
      connectedCallback() {
        const select = this.querySelector('[data-brp-shipping-country]');
        const country = document.documentElement.dataset.brpSelectedShippingCountry || select?.value || 'US';
        document.documentElement.dataset.brpSelectedShippingCountry = country;
        syncCountry(country);
      }
    });
  }
  customElements.define('brp-cart-shipping-guard', class extends HTMLElement {
    connectedCallback() {
      this.onInput = (event) => {
        if (event.target.form && event.target.form.id === this.dataset.formId) this.update();
      };
      this.onSubmit = (event) => {
        if (event.target.id !== this.dataset.formId) return;
        const wasCheckout = event.submitter?.name === 'checkout';
        const state = this.update();
        if (state.quoteRequired && wasCheckout) {
          event.preventDefault();
          const quoteButton = this.querySelector('[data-brp-table-required-button]');
          if (quoteButton && !quoteButton.disabled) event.target.requestSubmit(quoteButton);
          return;
        }
        event.target.dataset.brpNativeSubmitPending = 'true';
      };
      document.addEventListener('input', this.onInput);
      document.addEventListener('change', this.onInput);
      document.addEventListener('submit', this.onSubmit, true);
      this.observer = new MutationObserver(() => this.update());
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('input', this.onInput);
      document.removeEventListener('change', this.onInput);
      document.removeEventListener('submit', this.onSubmit, true);
      this.observer?.disconnect();
    }

    update() {
      const form = document.getElementById(this.dataset.formId);
      if (!form) return { active: false, quoteRequired: false };
      if (this.form !== form) {
        this.observer.disconnect();
        this.observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        this.form = form;
      }
      const countryField = form.elements.namedItem('attributes[brp_shipping_country]');
      const country = document.documentElement.dataset.brpSelectedShippingCountry || countryField?.value || 'US';
      document.documentElement.dataset.brpSelectedShippingCountry = country;
      syncCountry(country);
      const groups = new Map();
      let active = this.dataset.hasTable === 'true';
      let quoteRequired = country === 'CA' || this.dataset.serverQuote === 'true';
      for (const input of form.elements) {
        if (input.name !== 'updates[]' || input.dataset.brpShippingActive !== 'true') continue;
        active = true;
        const family = input.dataset.brpShippingFamily?.trim();
        const max = Number(input.dataset.brpShippingMax);
        const quantityValue = input.value.trim();
        const quantity = Number(quantityValue);
        if (!quantityValue || !Number.isInteger(quantity) || quantity < 0) {
          quoteRequired = true;
          continue;
        }
        if (input.dataset.brpShippingQuoteRequired === 'true') {
          if (quantity > 0) quoteRequired = true;
          continue;
        }
        if (input.dataset.brpShippingWeightMode === 'true') {
          if (quantity === 0) continue;
          const maxWeight = milliPounds(input.dataset.brpShippingMaxWeightLb);
          const unitWeight = milliPounds(input.dataset.brpShippingUnitWeightLb);
          if (!family || input.dataset.brpShippingWeightValid !== 'true' || !maxWeight || maxWeight % 1000 !== 0 || !unitWeight || unitWeight > maxWeight) {
            quoteRequired = true;
            continue;
          }
          const previous = groups.get(family);
          if (previous && (previous.mode !== 'weight' || previous.max !== maxWeight)) quoteRequired = true;
          const group = { mode: 'weight', max: maxWeight, quantity: (previous?.quantity || 0) + quantity, weight: (previous?.weight || 0) + unitWeight * quantity };
          if (!Number.isSafeInteger(group.weight) || group.weight > maxWeight) quoteRequired = true;
          groups.set(family, group);
          continue;
        }
        if (!family || !Number.isInteger(max) || max < 1 || max > 10) {
          quoteRequired = true;
          continue;
        }
        const previous = groups.get(family);
        if (previous && (previous.mode !== 'quantity' || previous.max !== max)) quoteRequired = true;
        const group = { mode: 'quantity', max, quantity: (previous?.quantity || 0) + quantity };
        if (group.quantity > max) quoteRequired = true;
        groups.set(family, group);
      }
      this.toggleAttribute('data-brp-table-cart', active);
      document.querySelectorAll(`[data-brp-express-for="${this.dataset.formId}"]`).forEach((express) => {
        express.hidden = quoteRequired || active;
      });
      this.querySelector('[data-brp-table-quote-panel]').hidden = !quoteRequired;
      this.querySelector('[data-brp-table-checkout-panel]').hidden = quoteRequired;
      const loading = !!form.querySelector('.cart__items--disabled');
      const hasItems = this.hasAttribute('data-notification') ? this.dataset.itemCount !== '0' : !!form.querySelector('input[name="updates[]"]');
      const required = this.querySelector('[data-brp-table-required-button]');
      required.disabled = !quoteRequired || loading || !hasItems;
      required.toggleAttribute('data-brp-quote-required', quoteRequired);
      required.toggleAttribute('data-brp-quote-submit', quoteRequired);
      const checkout = this.querySelector('[data-brp-table-checkout-button]');
      checkout.disabled = quoteRequired || loading || !hasItems;
      if (quoteRequired) checkout.removeAttribute('name');
      else checkout.name = 'checkout';
      const optional = this.querySelector('[data-brp-table-optional-button]');
      optional.hidden = !active || quoteRequired;
      optional.disabled = !active || quoteRequired || loading;
      optional.toggleAttribute('data-brp-quote-submit', active && !quoteRequired);
      return { active, quoteRequired, groups, country };
    }
  });
}
